import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Plus,
  Check,
  AlertTriangle,
  Package,
  Loader2,
  Sparkles,
  Building2,
  Search
} from 'lucide-react';
import ProductCreationDrawer from './ProductCreationDrawer';
import EnhancedProductLookup from './EnhancedProductLookup';
import supplierService from '../services/supplierService';

const ContractImportReviewDialog = ({
  open,
  onClose,
  contracts,
  onConfirm,
  isCreating
}) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [createdProducts, setCreatedProducts] = useState({});
  const [aiGenerating, setAiGenerating] = useState(null);
  
  // Manual match dialog state
  const [manualMatchDialog, setManualMatchDialog] = useState({
    open: false,
    contractIdx: null,
    item: null
  });
  
  // Suppliers state
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [editingContractIdx, setEditingContractIdx] = useState(null);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [editedContracts, setEditedContracts] = useState(contracts);

  // Load suppliers on mount
  useEffect(() => {
    const loadSuppliers = async () => {
      setLoadingSuppliers(true);
      try {
        const data = await supplierService.getSuppliers();
        setSuppliers(data || []);
      } catch (error) {
        console.error('Failed to load suppliers:', error);
      } finally {
        setLoadingSuppliers(false);
      }
    };
    
    if (open) {
      loadSuppliers();
      setEditedContracts(contracts);
    }
  }, [open, contracts]);

  // Handle supplier selection
  const handleSupplierChange = (contractIdx, supplier) => {
    setEditedContracts(prev => {
      const updated = [...prev];
      updated[contractIdx] = {
        ...updated[contractIdx],
        supplier_id: supplier._id || supplier.id,
        supplier_name: supplier.supplier_name,
        supplier_contact: supplier.contact_info || {}
      };
      return updated;
    });
    setEditingContractIdx(null);
    setSupplierSearch('');
  };

  // Filter suppliers based on search
  const filteredSuppliers = supplierSearch
    ? suppliers.filter(s => 
        s.supplier_name?.toLowerCase().includes(supplierSearch.toLowerCase())
      )
    : suppliers;

  const allItemsMatched = editedContracts.every(contract =>
    contract.items.every(item => item.matched_product_id || createdProducts[item.material_name + item.specification])
  );

  const handleCreateProduct = useCallback((item) => {
    setSelectedItem(item);
    setProductDialogOpen(true);
  }, []);

  // AI Generate and match product
  const handleAIGenerateAndMatch = useCallback(async (item) => {
    setAiGenerating(item.material_name + item.specification);

    try {
      const response = await fetch('/api/ai/generate-product-id', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          product_name: item.material_name,
          product_specification: item.specification || '',
          material: item.material || ''
        })
      });

      const data = await response.json();

      if (data.success && data.product_id) {
        // Search for existing product with this product_id
        const searchResponse = await fetch(
          `/api/products?product_id=${encodeURIComponent(data.product_id)}`
        );
        const searchData = await searchResponse.json();

        if (searchData.products && searchData.products.length > 0) {
          // Found existing product - match it
          const existingProduct = searchData.products[0];
          const key = item.material_name + item.specification;
          setCreatedProducts(prev => ({
            ...prev,
            [key]: existingProduct._id || existingProduct.id
          }));
        } else {
          // No existing product - open creation dialog with AI-generated product_id
          setSelectedItem({
            ...item,
            ai_generated_product_id: data.product_id
          });
          setProductDialogOpen(true);
        }
      } else {
        // AI generation failed - fall back to manual creation
        handleCreateProduct(item);
      }
    } catch (error) {
      console.error('AI generation error:', error);
      // Fall back to manual creation
      handleCreateProduct(item);
    } finally {
      setAiGenerating(null);
    }
  }, [handleCreateProduct]);

  const handleProductCreated = useCallback((product) => {
    if (selectedItem) {
      const key = selectedItem.material_name + selectedItem.specification;
      setCreatedProducts(prev => ({
        ...prev,
        [key]: product._id || product.id || product.product_id
      }));
    }
    setProductDialogOpen(false);
    setSelectedItem(null);
  }, [selectedItem]);

  // Manual match handlers
  const handleOpenManualMatch = (item, contractIdx) => {
    setManualMatchDialog({
      open: true,
      contractIdx,
      item
    });
  };

  const handleManualMatch = (selectedProductsList) => {
    if (!selectedProductsList || selectedProductsList.length === 0) return;

    const newProduct = selectedProductsList[0];
    const { contractIdx, item } = manualMatchDialog;

    if (newProduct._id) {
      setEditedContracts(prev => {
        const updated = [...prev];
        const targetContract = updated[contractIdx];
        const targetItem = targetContract.items.find(
          i => i.material_name === item.material_name && i.specification === item.specification
        );
        if (targetItem) {
          targetItem.matched_product_id = newProduct._id;
          targetItem.matched_product_code = newProduct.product_id;
          targetItem.matched_product_name = newProduct.name;
        }
        return updated;
      });
    }

    setManualMatchDialog({ open: false, contractIdx: null, item: null });
  };

  const handleConfirm = () => {
    const updatedContracts = editedContracts.map(contract => ({
      ...contract,
      items: contract.items.map(item => ({
        ...item,
        matched_product_id: item.matched_product_id ||
          createdProducts[item.material_name + item.specification] ||
          null
      }))
    }));
    onConfirm(updatedContracts);
  };

  const getMatchBadge = (item) => {
    const key = item.material_name + item.specification;
    const hasMatch = item.matched_product_id || createdProducts[key];

    if (hasMatch) {
      return (
        <Badge variant="default" className="bg-green-500/20 text-green-700 border-green-500/50">
          <Check className="w-3 h-3 mr-1" />
          已匹配
        </Badge>
      );
    }

    return (
      <Badge variant="outline" className="text-amber-600 border-amber-500/50 bg-amber-500/10">
        <AlertTriangle className="w-3 h-3 mr-1" />
        未匹配
      </Badge>
    );
  };

  const getSpecFromItem = (item) => {
    if (!item) return '-';
    return item.specification || item.matched_specification || '-';
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>确认导入合同物料</DialogTitle>
            <DialogDescription>
              以下是待导入的合同物料清单，请确认所有物料已匹配产品后创建采购订单
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto max-h-[60vh] space-y-4">
            {editedContracts.map((contract, idx) => (
              <Card key={idx} className="border-l-4 border-l-primary">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      {editingContractIdx === idx ? (
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <Input
                              placeholder="搜索供应商..."
                              value={supplierSearch}
                              onChange={(e) => setSupplierSearch(e.target.value)}
                              className="w-64"
                              autoFocus
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingContractIdx(null);
                                setSupplierSearch('');
                              }}
                            >
                              取消
                            </Button>
                          </div>
                          <div className="max-h-40 overflow-y-auto border rounded-md">
                            {loadingSuppliers ? (
                              <div className="p-2 text-sm text-muted-foreground">加载中...</div>
                            ) : filteredSuppliers.length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground">未找到供应商</div>
                            ) : (
                              filteredSuppliers.map((supplier) => (
                                <div
                                  key={supplier._id || supplier.id}
                                  className="p-2 hover:bg-muted cursor-pointer border-b last:border-0"
                                  onClick={() => handleSupplierChange(idx, supplier)}
                                >
                                  <div className="font-medium">{supplier.supplier_name}</div>
                                  {supplier.contact_info?.contact_person && (
                                    <div className="text-xs text-muted-foreground">
                                      联系人: {supplier.contact_info.contact_person}
                                    </div>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <h4 className="font-medium">{contract.supplier_name}</h4>
                            <p className="text-sm text-muted-foreground">
                              {contract.items.length} 项物料 • {contract.source_file}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingContractIdx(idx)}
                            className="ml-2"
                          >
                            <Search className="w-3 h-3 mr-1" />
                            修改供应商
                          </Button>
                        </div>
                      )}
                    </div>
                    <Badge variant="secondary">
                      {contract.quote_date}
                    </Badge>
                  </div>

                  <div className="space-y-2">
                    {contract.items.map((item, itemIdx) => {
                      const key = item.material_name + item.specification;
                      const isMatched = item.matched_product_id || createdProducts[key];

                      return (
                        <div
                          key={itemIdx}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            isMatched
                              ? 'bg-green-50/50 border-green-200'
                              : 'bg-amber-50/50 border-amber-200'
                          }`}
                        >
                          <div className="flex-1 grid grid-cols-5 gap-4 items-center">
                            <div className="col-span-2">
                              <div className="flex items-center gap-2">
                                <Package className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">{item.material_name}</span>
                              </div>
                              <p className="text-sm text-muted-foreground ml-6">
                                规格: {getSpecFromItem(item)}
                              </p>
                            </div>

                            <div className="text-center">
                              <p className="text-sm text-muted-foreground">数量</p>
                              <p className="font-medium">{item.quantity} {item.unit}</p>
                            </div>

                            <div className="text-center">
                              <p className="text-sm text-muted-foreground">单价</p>
                              <p className="font-medium">¥{item.unit_price?.toFixed(2)}</p>
                            </div>

                            <div className="text-center">
                              {getMatchBadge(item)}
                            </div>
                          </div>

                          {!isMatched && (
                            <div className="flex gap-2 ml-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenManualMatch(item, idx)}
                                disabled={isCreating}
                              >
                                <Search className="w-4 h-4 mr-1" />
                                匹配
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAIGenerateAndMatch(item)}
                                disabled={isCreating || aiGenerating === (item.material_name + item.specification)}
                              >
                                {aiGenerating === (item.material_name + item.specification) ? (
                                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Sparkles className="w-4 h-4 mr-1 text-purple-500" />
                                )}
                                AI生成
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCreateProduct(item)}
                                disabled={isCreating}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                创建产品
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}

            {!allItemsMatched && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">有物料未匹配产品</span>
                </div>
                <p className="text-sm text-amber-700 mt-1">
                  请点击"创建产品"为未匹配的物料创建新产品，或匹配现有产品后才能创建采购订单。
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose} disabled={isCreating}>
              取消
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={!allItemsMatched || isCreating}
            >
              {isCreating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              创建采购订单
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Creation Drawer */}
      <ProductCreationDrawer
        open={productDialogOpen}
        onClose={() => setProductDialogOpen(false)}
        initialData={{
          name: selectedItem?.suggested_product_name || selectedItem?.material_name || '',
          product_id: selectedItem?.suggested_product_id || (() => {
            // Fallback: generate product_id from extracted fields
            const materialGrade = selectedItem?.extracted_material_grade || selectedItem?.material_grade || '';
            const tipDiameter = selectedItem?.extracted_tip_diameter || selectedItem?.tip_diameter;
            const totalLength = selectedItem?.extracted_total_length || selectedItem?.total_length;
            
            if (tipDiameter && totalLength) {
              if (materialGrade) {
                return `RM-${materialGrade}-${tipDiameter}-${totalLength}`;
              }
              return `RM-${tipDiameter}-${totalLength}`;
            }
            
            // Try to parse from specification as last resort
            const specMatch = selectedItem?.specification?.match(/R(\d+\.?\d*)\*(\d+)/);
            if (specMatch && materialGrade) {
              return `RM-${materialGrade}-${specMatch[1]}-${specMatch[2]}`;
            }
            if (specMatch) {
              return `RM-${specMatch[1]}-${specMatch[2]}`;
            }
            return '';
          })(),
          specification: getSpecFromItem(selectedItem),
          category: '原材料',
          product_type: 'raw_material',
          unit: selectedItem?.unit || '件',
          tip_diameter: selectedItem?.extracted_tip_diameter || selectedItem?.tip_diameter || '',
          total_length: selectedItem?.extracted_total_length || selectedItem?.total_length || '',
          material_grade: selectedItem?.extracted_material_grade || selectedItem?.material_grade || ''
        }}
        onProductCreated={handleProductCreated}
      />

      {/* Manual Match Dialog */}
      <EnhancedProductLookup
        open={manualMatchDialog.open}
        onClose={() => setManualMatchDialog({ open: false, contractIdx: null, item: null })}
        onAddProducts={handleManualMatch}
        title="手动匹配产品"
        initialSearchQuery={manualMatchDialog.item ? (
          manualMatchDialog.item.specification || 
          manualMatchDialog.item.product_id || 
          manualMatchDialog.item.material_name || ''
        ) : ''}
        ocrExtractedData={manualMatchDialog.item || null}
      />
    </>
  );
};

export default ContractImportReviewDialog;
